import { DashboardShell } from "@/components/dashboard-shell";
import { InsightCardSkeleton } from "@/components/analytics/insight-card-skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { SummarySkeleton } from "@/components/summary/summary-kit";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

/**
 * Shared building blocks for the per-route `loading.tsx` files. Each page
 * renders its own <DashboardShell>, so a loading file has to render the shell
 * too — otherwise the sidebar/header would vanish during navigation. The
 * placeholders mirror the real loading states in src/components/**.
 */
export function RouteLoading({ children }: { children: React.ReactNode }) {
  return (
    <DashboardShell>
      <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
        {children}
      </div>
    </DashboardShell>
  );
}

/** Search box + filter + action row used by the list pages. */
export function ToolbarSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Skeleton className="h-8 w-full sm:w-64" />
      <Skeleton className="h-8 w-20" />
      <span className="flex-1" />
      <Skeleton className="h-8 w-28" />
    </div>
  );
}

/** /customers card layout. */
export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-44 rounded-xl" />
      ))}
    </div>
  );
}

/** /contacts table layout. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="rounded-xl border bg-card">
      <Table>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
              <TableCell className="px-4">
                <Skeleton className="h-8 w-full" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** /deals board layout. */
export function KanbanLoadingSkeleton() {
  return (
    <div className="flex gap-3 overflow-hidden pb-1.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex w-72 shrink-0 flex-col gap-1.5 rounded-2xl bg-muted p-1.5 max-md:w-[85%]"
        >
          <div className="px-1.5 pt-1.5 pb-0.5">
            <Skeleton className="h-4 w-24 bg-background/60" />
          </div>
          {Array.from({ length: 2 }).map((_, j) => (
            <Skeleton key={j} className="h-24 rounded-xl bg-card" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** /tasks board layout. */
export function TasksLoadingSkeleton() {
  return (
    <div className="flex gap-5 overflow-hidden p-1">
      {[3, 2, 1, 2].map((n, i) => (
        <div
          key={i}
          className="flex w-64 shrink-0 flex-col gap-3 rounded-xl bg-muted/40 p-2 ring-1 ring-foreground/10 max-md:w-[85%]"
        >
          <Skeleton className="h-6.5 w-24" />
          {Array.from({ length: n }).map((_, j) => (
            <Skeleton key={j} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** /activity timeline layout. */
export function FeedLoadingSkeleton() {
  return (
    <div className="mx-auto w-full max-w-2xl px-2 py-2 sm:px-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-20" />
        <span className="ms-auto" />
        <Skeleton className="h-7 w-28" />
      </div>
      <div className="mt-5 flex flex-col gap-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** /schedule calendar card. */
export function CalendarLoadingSkeleton() {
  return (
    <Card className="h-[calc(100svh-5.5rem)] min-h-[600px] flex-row gap-0 p-0 max-md:h-[70dvh] max-md:min-h-0">
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <Skeleton className="h-8 w-64 max-w-full" />
        <Skeleton className="min-h-0 flex-1" />
      </div>
    </Card>
  );
}

/** Metric strip used by /analytics and /leads. */
export function MetricStripSkeleton({ count = 5 }: { count?: number }) {
  return (
    <Card>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Title + period controls above the insight pages. */
export function InsightsHeaderSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Skeleton className="h-7 w-48" />
      <span className="flex-1" />
      <Skeleton className="h-8 w-40" />
    </div>
  );
}

/** /analytics layout: filter bar, section tabs, then the Overview grid. */
export function AnalyticsLoadingSkeleton() {
  return (
    <>
      <InsightsHeaderSkeleton />
      <Skeleton className="h-11 w-full max-w-md" />
      <SummarySkeleton />
    </>
  );
}

/** /campaigns layout: filter bar, then the summary grid. */
export function CampaignsLoadingSkeleton() {
  return (
    <>
      <InsightsHeaderSkeleton />
      <SummarySkeleton />
    </>
  );
}

/** /inventory layout: view tabs, toolbar, then the summary grid. */
export function InventoryLoadingSkeleton() {
  return (
    <>
      <Skeleton className="h-11 w-full max-w-md" />
      <InsightsHeaderSkeleton />
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton className="h-80 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    </>
  );
}

/** /leads layout. */
export function LeadsLoadingSkeleton() {
  return (
    <>
      <MetricStripSkeleton count={4} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InsightCardSkeleton title="Leads over time" />
        <InsightCardSkeleton title="Lead funnel" />
      </div>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[3fr_2fr]">
        <InsightCardSkeleton title="Recent leads" variant="list" />
        <InsightCardSkeleton title="Leads by region" variant="list" />
      </div>
    </>
  );
}

/** /customers/[id] layout. */
export function CustomerDetailLoadingSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-2 py-2 sm:px-4">
      <Skeleton className="h-8 w-28" />
      <div className="flex items-start gap-3">
        <Skeleton className="size-14 rounded-2xl" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-52" />
        </div>
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-9 w-96 max-w-full" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-6">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
        <div className="flex flex-col gap-6">
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}
